/**
 * Import Monitor — Scheduled Jobs tab (CR-8213).
 *
 * The run log of nightly scripts — e.g. auto-acknowledging standard suppliers'
 * PO lines — kept for 30 days, newest first. Each row shows what the run did
 * at a glance; opening it shows the full line-by-line table and report.
 * Customers see their own runs; Helix operators also see platform-wide ones.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { scheduledJobsAPI, type ScheduledJobRunDetail, type ScheduledJobsResponse } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CommandRunResult } from '../../components/admin/settings/CommandRunResult';
import { AlertCircle, ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface ImportScheduledJobsTabProps {
  customerId: number;
}

const PAGE_SIZE = 25;

const STATUS_CLASS: Record<string, string> = {
  success: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
  running: 'bg-blue-100 text-blue-800',
  queued: 'bg-slate-100 text-slate-700',
};

/** "2.4s" / "1m 12s" — a run's wall-clock time. */
function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

export default function ImportScheduledJobsTab({ customerId }: ImportScheduledJobsTabProps) {
  const { formatDateTime } = useFormatters();
  const [data, setData] = useState<ScheduledJobsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [command, setCommand] = useState('');
  const [status, setStatus] = useState('');
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ScheduledJobRunDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Only the newest request may write state: a slow response for an older
  // filter, customer or run must never land on top of a newer one.
  const loadSeq = useRef(0);
  const detailSeq = useRef(0);

  // A different customer is a different log — start clean, never show the
  // previous customer's numbers or carry its filters and page across.
  useEffect(() => {
    setData(null);
    setCommand('');
    setStatus('');
    setPage(1);
  }, [customerId]);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await scheduledJobsAPI.getRuns(customerId, {
        command: command || undefined, status: status || undefined, days, page, pageSize: PAGE_SIZE,
      });
      if (seq === loadSeq.current) setData(res.data);
    } catch (err: unknown) {
      if (seq !== loadSeq.current) return;
      console.error('Scheduled jobs load failed:', err);
      setData(null);
      setError(getApiErrorMessage(err, 'Failed to load scheduled jobs'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [customerId, command, status, days, page]);

  useEffect(() => { load(); }, [load]);
  // A new customer, filter or page is a new list — close any open run.
  useEffect(() => {
    detailSeq.current += 1;
    setOpenId(null);
    setDetail(null);
    setDetailError(null);
  }, [customerId, command, status, days, page]);

  const toggleRun = async (runId: number) => {
    const seq = ++detailSeq.current;
    setDetail(null);
    setDetailError(null);
    if (openId === runId) { setOpenId(null); return; }
    setOpenId(runId);
    try {
      const res = await scheduledJobsAPI.getRun(customerId, runId);
      if (seq === detailSeq.current) setDetail(res.data);
    } catch (err: unknown) {
      if (seq === detailSeq.current) setDetailError(getApiErrorMessage(err, 'Failed to load the run'));
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Runs (last {data.summary.window_days} days)</p>
            <p className="text-2xl font-semibold tabular-nums">{data.summary.total.toLocaleString()}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Failed</p>
            <p className={`text-2xl font-semibold tabular-nums ${data.summary.failed ? 'text-red-600' : ''}`}>
              {data.summary.failed.toLocaleString()}
            </p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Last run</p>
            <p className="text-lg font-semibold">
              {data.summary.last_run_at ? formatDateTime(data.summary.last_run_at) : 'No runs yet'}
            </p>
          </CardContent></Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Job"
          className="text-sm border rounded-md py-2 px-2"
          value={command}
          onChange={(e) => { setCommand(e.target.value); setPage(1); }}
        >
          <option value="">All jobs</option>
          {(data?.jobs ?? []).map((job) => <option key={job.key} value={job.key}>{job.label}</option>)}
        </select>
        <select
          aria-label="Status"
          className="text-sm border rounded-md py-2 px-2"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
        </select>
        <select
          aria-label="Window"
          className="text-sm border rounded-md py-2 px-2"
          value={days}
          onChange={(e) => { setDays(parseInt(e.target.value, 10) || 30); setPage(1); }}
        >
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
        </select>
        <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} aria-label="Refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">
          Logs are kept for {data?.summary.retention_days ?? 30} days.
        </span>
      </div>

      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="flex items-center gap-2 p-6 text-sm text-red-700">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          ) : loading && !data ? (
            <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : !data || data.results.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No scheduled job runs in this window.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-left">
                    <th className="py-2 px-3 w-8" />
                    <th className="py-2 px-3 font-medium">Started</th>
                    <th className="py-2 px-3 font-medium">Job</th>
                    <th className="py-2 px-3 font-medium">Customer</th>
                    <th className="py-2 px-3 font-medium">Status</th>
                    <th className="py-2 px-3 font-medium text-right">Duration</th>
                    <th className="py-2 px-3 font-medium">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((run) => (
                    <Fragment key={run.id}>
                      <tr
                        className="border-b hover:bg-muted/40 cursor-pointer"
                        onClick={() => toggleRun(run.id)}
                      >
                        <td className="py-2 px-3">
                          {openId === run.id
                            ? <ChevronDown className="h-4 w-4" aria-label="Hide run" />
                            : <ChevronRight className="h-4 w-4" aria-label="Show run" />}
                        </td>
                        <td className="py-2 px-3 whitespace-nowrap">
                          {run.started_at ? formatDateTime(run.started_at) : '—'}
                        </td>
                        <td className="py-2 px-3">
                          {run.label}
                          {run.arguments?.['dry_run'] === true && (
                            <Badge variant="outline" className="ml-2">Dry run</Badge>
                          )}
                          {run.arguments?.['invoked_from'] === 'by_hand' && (
                            <Badge variant="outline" className="ml-2" title="Started by hand, not by the nightly schedule">
                              Run by hand
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 px-3">{run.customer_name ?? 'All customers'}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_CLASS[run.status] ?? ''}`}>
                            {run.status}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">{formatDuration(run.duration_seconds)}</td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {run.error
                            ? <span className="text-red-700">{run.error}</span>
                            : run.summary.map((tile) => `${tile.label}: ${tile.value}`).join(' · ') || '—'}
                        </td>
                      </tr>
                      {openId === run.id && (
                        <tr className="border-b bg-muted/20">
                          <td colSpan={7} className="p-4">
                            {detailError ? (
                              <div className="flex items-center gap-2 text-sm text-red-700">
                                <AlertCircle className="h-4 w-4" /> {detailError}
                              </div>
                            ) : !detail ? (
                              <div className="flex items-center text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading run…
                              </div>
                            ) : (
                              <CommandRunResult result={detail.result} stdout={detail.stdout} />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data && data.count > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className="tabular-nums">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
